import styled from "styled-components";
import media from "styled-media-query";
import ReactPlayer from "react-player";
import { Modal, Button, Text, ColorV2 } from "@adoptaunabuelo/react-components";
import { Maximize2, Minimize2, X } from "lucide-react";
import { ReactElement, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { State } from "../../redux";
import DonationShield from "../../assets/images/icon/DonationShield";

const Container = styled.div`
	display: flex;
	flex-direction: row;
	${media.lessThan("medium")`
        flex-direction: column;
    `}
`;
const LeftView = styled.div<{ $fullSize?: boolean }>`
	display: flex;
	width: 300px;
	aspect-ratio: 9/16;
	padding: 8px;
	border-radius: 24px;
	${(props) => media.lessThan("medium")`
		flex: 1;
        justify-content: center;
        width: unset;
        aspect-ratio: unset;
		min-height: ${props.$fullSize ? "80%" : "50%"};
		transition: min-height 0.4s ease-in-out;
		padding: 0px;
		border-radius: 0px;
    `}
`;
const RightView = styled.div`
	position: relative;
	display: flex;
	flex: 1;
	flex-direction: column;
	padding: 32px;
	text-align: center;
	${media.lessThan("medium")`
		padding: 24px 24px 16px;
	`}
`;
const DataContainer = styled.div`
	display: flex;
	flex: 1;
	flex-direction: column;
	justify-content: center;
`;
const ButtonContainer = styled.div`
	display: flex;
	flex-direction: row;
	justify-content: center;
	gap: 24px;
	${media.lessThan("medium")`
		position: sticky;
    	bottom: 16px;
	`}
`;
const ChildrenContainer = styled.div`
	margin-top: 24px !important;
	margin-bottom: 24px !important;
	${media.lessThan("medium")`
		margin-top: 12px !important;
		margin-bottom: 24px !important;
	`}
`;
const DesktopView = styled.div`
	display: flex;
	${media.lessThan("medium")`
		display: none;
	`}
`;
const MobileView = styled.div`
	display: none;
	padding: 0px 16px 16px;
	position: absolute;
	bottom: 0px;
	left: 0px;
	right: 0px;
	justify-content: space-between;
	z-index: 10;
	${media.lessThan("medium")`
		display: flex;
	`}
`;
const VideoView = styled.div`
	position: relative;
	width: 100%;
	height: 100%;
	background-color: black;
	border-radius: 8px;
	overflow: hidden;
	${media.lessThan("medium")`
		border-radius: 0px;
	`}
`;
const SuccessButton = styled(Button)<{ isVisible: boolean }>`
	flex: 1;
	opacity: ${(props) => (props.isVisible ? 1 : 0)};
	transform: ${(props) =>
		props.isVisible ? "translateY(0px)" : "translateY(10px)"};
	transition: opacity 0.6s ease, transform 0.6s ease;
	${media.lessThan("medium")`
		width: 100%;
	`}
`;
const ProtectedDonationContainer = styled.div`
	display: flex;
	flex-direction: row;
	padding: 12px 16px;
	background-color: ${ColorV2.border.primarySoft};
	border-radius: 16px;
	gap: 12px;
	margin-bottom: 32px;
	align-items: center;
`;
const DonationIcon = styled.div`
	height: 40px;
	width: 40px;
	min-height: 40px;
	min-width: 40px;
`;

const SkipModal = (props: SkipModalProps) => {
	const appLocation = useSelector((state: State) => state.location.location);
	const isMobile = useSelector((state: State) => state.screen.isMobile);
	const translation = useSelector(
		(state: State) => state.location.translation
	);
	const [disableSkip, setDisableSkip] = useState(true);
	const [fullSizeVideo, setFullSizeVideo] = useState(
		props.video ? true : false
	);

	useEffect(() => {
		if (props.isVisible) {
			setDisableSkip(true);
		}
	}, [props.isVisible]);

	return (
		<Modal
			style={
				isMobile ? { height: "100%", maxHeight: "95%" } : { width: 900 }
			}
			contentStyle={
				isMobile
					? { padding: 0, height: "100%", display: "flex" }
					: { padding: 0 }
			}
			isVisible={props.isVisible}
			onClose={props.onClose}
			hideClose={true}
			hideHeader={true}
			type={props.type}
		>
			<Container>
				<LeftView $fullSize={fullSizeVideo}>
					<VideoView>
						<ReactPlayer
							width={"100%"}
							height={"100%"}
							src={props.video}
							playing={true}
							playsInline={true}
							volume={1}
							controls={false}
							config={{
								html: {
									forceVideo: true,
									attributes: {
										controlsList: "nodownload nofullscreen",
										disablepictureinpicture: true,
									},
								},
							}}
							onEnded={props.onVideoEnded && props.onVideoEnded}
						/>
						<MobileView>
							<Button
								style={{
									backgroundColor: "white",
									borderRadius: 80,
								}}
								design="image"
								icon={
									fullSizeVideo ? (
										<Minimize2
											color={ColorV2.text.neutralHard}
										/>
									) : (
										<Maximize2
											color={ColorV2.text.neutralHard}
										/>
									)
								}
								onClick={() => setFullSizeVideo(!fullSizeVideo)}
							/>
							<Button
								style={{
									display: isMobile ? "block" : "none",
									backgroundColor: "white",
									color: ColorV2.text.neutralHard,
									height: 42,
									padding: "0px 16px",
								}}
								countdown={props.duration ? props.duration : 10}
								onClick={props.onSkip}
								onCountdownEnd={() => {
									setDisableSkip(false);
								}}
							>
								<Text
									type="b2"
									style={{
										color: ColorV2.text.neutralHard,
									}}
								>
									{disableSkip
										? translation.component_modal_skip_payment_button_video_01
										: translation.component_modal_skip_payment_button_skip}
								</Text>
							</Button>
						</MobileView>
					</VideoView>
				</LeftView>
				<RightView>
					{!isMobile && (
						<Button
							style={{ position: "absolute", top: 8, right: 16 }}
							design="image"
							icon={<X />}
							disabled={disableSkip}
							onClick={props.onClose}
						/>
					)}
					<DataContainer>
						<Text
							type="h2"
							weight="semibold"
							style={{ textAlign: "left" }}
						>
							{props.title
								? props.title
								: "¡Espera! Con una pequeña ayuda podemos hacer mucho"}
						</Text>
						{!props.hideAlert && (
							<Text
								type="p2"
								style={{
									padding: "12px 16px",
									borderRadius: 8,
									backgroundColor: ColorV2.surface.background,
									textAlign: "left",
									marginTop: 24,
								}}
							>
								Somos una Fundación independiente y no recibimos
								subvenciones. Nuestra actividad se sostiene
								gracias al apoyo de personas comprometidas con
								las personas mayores.
							</Text>
						)}
						<ChildrenContainer>{props.children}</ChildrenContainer>
						{props.message && (
							<ProtectedDonationContainer>
								<DonationIcon>
									<DonationShield size="large" />
								</DonationIcon>
								<Text type="p2" style={{ textAlign: "left" }}>
									<span style={{ fontWeight: 600 }}>
										Donación protegida.
									</span>{" "}
									{props.message}
								</Text>
							</ProtectedDonationContainer>
						)}
					</DataContainer>
					<ButtonContainer>
						<DesktopView>
							<Button
								style={{
									backgroundColor: "transparent",
									color: ColorV2.text.neutralHard,
									width: "100%",
									padding: 0,
									justifyContent: "flex-start",
								}}
								disabled={disableSkip}
								countdown={props.duration ? props.duration : 10}
								onClick={props.onSkip}
								onCountdownEnd={() => {
									setDisableSkip(false);
								}}
							>
								{disableSkip
									? "Saltar en "
									: translation.component_modal_skip_payment_button_skip}
							</Button>
						</DesktopView>
						<SuccessButton
							isVisible={!disableSkip}
							onClick={() =>
								props.onClick({
									price:
										props.charge === "one"
											? 5
											: props.minPrice,
									charge:
										props.charge === "one"
											? "unique"
											: "monthly",
								})
							}
						>
							Quiero donar{" "}
							{new Intl.NumberFormat(
								appLocation?.language || "es-ES",
								{
									style: "currency",
									currency:
										appLocation?.currency.name || "eur",
									maximumFractionDigits: 0,
								}
							).format(props.minPrice)}{" "}
							{props.charge === "recurrent" && "al mes"}
						</SuccessButton>
					</ButtonContainer>
				</RightView>
			</Container>
		</Modal>
	);
};
export default SkipModal;
export interface SkipModalProps {
	isVisible: boolean;
	title?: string;
	type?: "full-screen" | "default";
	charge: "recurrent" | "one";
	children?: ReactElement;
	message?: string;
	video: string;
	duration?: number;
	minPrice: number;
	hideAlert?: boolean;
	onVideoEnded?: () => void;
	onClose: () => void;
	onSkip: () => void;
	onClick: (product: { price: number; charge: string }) => void;
}
